import { Datasets, LCTOnlyDatasets } from "../constants/enums"
import {
    DEMAND_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT,
    DEMAND_COMPLIANCE_STATUS_ID_NON_COMPLIANT,
    DRAFT_DEMAND_DEVICES_STATUS_ID,
    DRAFT_GEN_DEVICES_STATUS_ID,
    GEN_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT,
    GEN_COMPLIANCE_STATUS_ID_NON_COMPLIANT,
    HEAT_PUMP,
} from "../constants/generalConstants"

export const BASE_URL = "/"
export const LOGIN = "/login"
export const RESET_PASSWORD = "/reset-password"
export const SIGN_OUT_SUCCESS = "/sign-out-success"
export const NOT_FOUND_ROUTE = "/*"

// ******************************************************************
// Register View Routes
// ******************************************************************/

export const DEVICE_DATABASE_BASE = "/device-databases"
export const GEN_DEVICE_SEARCH = `${DEVICE_DATABASE_BASE}/search-gen`
export const DEMAND_DEVICE_SEARCH_BASE = `${DEVICE_DATABASE_BASE}/search-demand`
export const DEMAND_DEVICE_SEARCH = `${DEMAND_DEVICE_SEARCH_BASE}/:dataset`
export const SINGLE_GEN_DEVICE_BASE = `${DEVICE_DATABASE_BASE}/generation-device`
export const SINGLE_GEN_DEVICE = `${SINGLE_GEN_DEVICE_BASE}/:deviceId`
export const SINGLE_GEN_DEVICE_DRAFT = `${SINGLE_GEN_DEVICE_BASE}/draft/:deviceId`
export const SINGLE_DEMAND_DEVICE_BASE = `${DEVICE_DATABASE_BASE}/demand-device/dataset`
export const SINGLE_DEMAND_DEVICE = `${SINGLE_DEMAND_DEVICE_BASE}/:dataset/id/:deviceId`
export const SINGLE_DEMAND_DEVICE_DRAFT = `${SINGLE_DEMAND_DEVICE_BASE}/draft/:dataset/id/:deviceId`
export const ASSESS_DEMAND_DEVICE_BASE = `${DEVICE_DATABASE_BASE}/assess-demand-device/dataset`
export const ASSESS_DEMAND_DEVICE = `${ASSESS_DEMAND_DEVICE_BASE}/:dataset/id/:deviceId`

// ******************************************************************
// Installer web portal routes
// ******************************************************************/
export const NEW_SINGLE_APPLICATION = "/new-application"
export const AMEND_SINGLE_APPLICATION_BASE = "/amend-application"
export const AMEND_SINGLE_APPLICATION = `${AMEND_SINGLE_APPLICATION_BASE}/:applicationId`
export const INSTALLER_SINGLE_APPLICATION_BASE = "/installer-application"
export const INSTALLER_SINGLE_APPLICATION = `${INSTALLER_SINGLE_APPLICATION_BASE}/:applicationId`
export const INSTALLER_LIST_APPLICATIONS = "/installer-applications"
export const COMMISSIONING_FORM_BASE = `/commissioning`
export const COMMISSIONING_FORM = `${COMMISSIONING_FORM_BASE}/:applicationId`

// ******************************************************************
// Installer Drafts routes
// ******************************************************************/
export const INSTALLER_DRAFTS = "/my-drafts"

// ******************************************************************
// Admin routes
// ******************************************************************/
export const ADMIN_PORTAL = "/admin-manage-users"
export const ADMIN_MY_ORGANISATION = "/admin-my-organisation"

// Developer links
export const DEV_PORTAL = "/developer"

// DNO web portal routes
export const DNO_APPLICATIONS_NOTIFICATIONS = "/dno-all-applications"
export const DNO_SOLAR_NOTIFICATIONS = "/dno-plug-in-solar-notifications"
export const DNO_APPLICATIONS_ESCALATIONS = "/dno-escalations"
export const DNO_APPLICATIONS_WORKS_REQUIRED = "/dno-works-required"
export const DNO_SINGLE_APPLICATION_BASE = "/dno-application"
export const DNO_SINGLE_APPLICATION = `${DNO_SINGLE_APPLICATION_BASE}/:applicationId`
export const DNO_THRESHOLD_MANAGEMENT = "/dno-threshold-management"

// Homepage dashboard routes
export const NON_COMPLIANT_DEVICES = `${GEN_DEVICE_SEARCH}/?${GEN_COMPLIANCE_STATUS_ID_NON_COMPLIANT}`
export const AWAITING_ASSESSMENT_DEVICES = `${GEN_DEVICE_SEARCH}/?${GEN_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT}`
export const DEM_NON_COMPLIANT_DEVICES = `${DEMAND_DEVICE_SEARCH_BASE}/${HEAT_PUMP}?${DEMAND_COMPLIANCE_STATUS_ID_NON_COMPLIANT}`
// eslint-disable-next-line max-len
export const DEM_AWAITING_ASSESSMENT_DEVICES = `${DEMAND_DEVICE_SEARCH_BASE}/${HEAT_PUMP}?${DEMAND_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT}`

// V2G assessor routes — deep-link into the EVAC device register with the
// V2G + Awaiting Assessment filters preapplied. V2G assessors are scoped to
// V2G rows at the API layer, so the URL params are belt-and-braces.
// eslint-disable-next-line max-len
export const V2G_AWAITING_ASSESSMENT_DEVICES = `${DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.EVAC}?device_type=V2G&${DEMAND_COMPLIANCE_STATUS_ID_AWAITING_ASSESSMENT}`
export const MANU_DRAFT_DEVICES = `${GEN_DEVICE_SEARCH}/?${DRAFT_GEN_DEVICES_STATUS_ID}`
export const DRAFT_HP_DEVICES = `${DEMAND_DEVICE_SEARCH_BASE}/${HEAT_PUMP}?${DRAFT_DEMAND_DEVICES_STATUS_ID}`
export const DRAFT_EB_DEVICES = `${DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.EB}/?${DRAFT_DEMAND_DEVICES_STATUS_ID}`
export const DRAFT_ACU_DEVICES = `${DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.ACU}/?${DRAFT_DEMAND_DEVICES_STATUS_ID}`
export const DRAFT_EVDC_DEVICES = `${DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.EVDC}/?${DRAFT_DEMAND_DEVICES_STATUS_ID}`
export const DRAFT_EVAC_DEVICES = `${DEMAND_DEVICE_SEARCH_BASE}/${LCTOnlyDatasets.EVAC}/?${DRAFT_DEMAND_DEVICES_STATUS_ID}`

// Fallback UI
export const NOT_AUTHORISED = "not-authorised"

// Learn more / FAQ
export const LEARN_MORE = "/learn-more"

// Company Lookup
export const COMPANY_SEARCH = "/company-search"

// Insights URL
export const INSIGHTS = "/insights"

// ******************************************************************
// Legal pages
// ******************************************************************/
export const PRIVACY_POLICY = "/privacy-policy"
export const TERMS_OF_USE = "/terms-of-use"
export const COOKIE_POLICY = "/cookie-policy"
export const CHANGE_LOG = "/change-log"

// Nav links for CS routing
export const DEMAND_DEVICE_SEARCH_NAV_LINK = (
    dataset: LCTOnlyDatasets
): string => `${DEMAND_DEVICE_SEARCH_BASE}/${dataset}`

export const SINGLE_GEN_DEVICE_NAV_LINK = (deviceSysRef: string): string =>
    `${SINGLE_GEN_DEVICE_BASE}/${encodeURIComponent(deviceSysRef)}`

export const SINGLE_GEN_DEVICE_DRAFT_NAV_LINK = (
    deviceSysRef: string
): string =>
    `${SINGLE_GEN_DEVICE_BASE}/draft/${encodeURIComponent(deviceSysRef)}/`

export const SINGLE_DEMAND_DEVICE_NAV_LINK = (
    dataset: LCTOnlyDatasets,
    deviceSysRef: string
): string =>
    `${SINGLE_DEMAND_DEVICE_BASE}/${dataset}/id/${encodeURIComponent(
        deviceSysRef
    )}`

// TODO: TaiOla - Make this into an actual page
export const SINGLE_DEMAND_DEVICE_DRAFT_NAV_LINK = (
    dataset: LCTOnlyDatasets,
    deviceSysRef: string
): string =>
    `${SINGLE_DEMAND_DEVICE_BASE}/draft/${dataset}/id/${encodeURIComponent(
        deviceSysRef
    )}`

export const ASSESS_DEMAND_DEVICE_NAV_LINK = (
    dataset: LCTOnlyDatasets,
    deviceSysRef: string
): string =>
    `${ASSESS_DEMAND_DEVICE_BASE}/${dataset}/id/${encodeURIComponent(
        deviceSysRef
    )}`

export const SINGLE_DEVICE_NAV_LINK = (
    dataset: Datasets,
    deviceSysRef: string
): string => {
    if (dataset === Datasets.TTR || dataset === Datasets.TTR_DRAFT) {
        return SINGLE_GEN_DEVICE_NAV_LINK(deviceSysRef)
    }
    return SINGLE_DEMAND_DEVICE_NAV_LINK(
        dataset as unknown as LCTOnlyDatasets,
        deviceSysRef
    )
}

// ******************************************************************
// TTR Routes
// ******************************************************************/

export const TTR_AMEND_DEVICE_BASE = "/amend-device"
export const TTR_NEW_VERSION_BASE = "/new-version"

export const TTR_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE = "/continue-amendment"
export const TTR_CONTINUE_DEVICE_VERSION_DRAFT_BASE = "/continue-version"

// TTR Device publish route
export const TTR_PUBLISH_DEVICE = "/publish-ttr-device"

// TTR Device amendment routes
export const TTR_AMEND_DEVICE = `/${TTR_AMEND_DEVICE_BASE}/:deviceRef`
export const TTR_CONTINUE_DEVICE_VERSION_DRAFT = `${TTR_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/:deviceRef`

// TTR Device amendment routes
export const TTR_NEW_VERSION = `/${TTR_NEW_VERSION_BASE}/:deviceRef`
export const TTR_CONTINUE_DEVICE_AMENDMENT_DRAFT = `${TTR_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/:deviceRef`

// Nav links for TTR routing
export const TTR_AMEND_DEVICE_NAV_LINK = (deviceRef: string): string =>
    `${TTR_AMEND_DEVICE_BASE}/${encodeURIComponent(deviceRef)}`

export const TTR_NEW_VERSION_NAV_LINK = (deviceRef: string): string =>
    `${TTR_NEW_VERSION_BASE}/${encodeURIComponent(deviceRef)}`

export const TTR_CONTINUE_DEVICE_VERSION_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${TTR_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/${encodeURIComponent(deviceRef)}`

export const TTR_CONTINUE_DEVICE_AMENDMENT_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${TTR_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/${encodeURIComponent(
        deviceRef
    )}`

// ******************************************************************
// LCT General Routes
// ******************************************************************/

export const PUBLISH_NEW_LCT_TTR_DEVICE = "/publish-device"

// ******************************************************************
// EVAC Routes
// ******************************************************************/
export const EVAC_AMEND_DEVICE_BASE = "/evac-amend-device"
export const EVAC_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE =
    "/evac-continue-amendment"
export const EVAC_CONTINUE_DEVICE_VERSION_DRAFT_BASE = "/evac-continue-version"
export const EVAC_NEW_VERSION_BASE = `/evac-new-version`

export const EVAC_PUBLISH_DEVICE = "/evac-publish-device"
export const V2G_BUNDLE_PUBLISH_DEVICE = `${PUBLISH_NEW_LCT_TTR_DEVICE}/v2g-bundle`
export const EVAC_AMEND_DEVICE = `${EVAC_AMEND_DEVICE_BASE}/:deviceRef`
export const EVAC_NEW_VERSION = `${EVAC_NEW_VERSION_BASE}/:deviceRef`

export const EVAC_CONTINUE_DEVICE_AMENDMENT_DRAFT = `${EVAC_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/:deviceRef`
export const EVAC_CONTINUE_DEVICE_VERSION_DRAFT = `${EVAC_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/:deviceRef`

// Nav links for EVAC routing
export const EVAC_AMEND_DEVICE_NAV_LINK = (deviceRef: string): string =>
    `${EVAC_AMEND_DEVICE_BASE}/${encodeURIComponent(deviceRef)}`

export const EVAC_CONTINUE_DEVICE_VERSION_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${EVAC_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/${encodeURIComponent(
        deviceRef
    )}`

export const EVAC_CONTINUE_DEVICE_AMENDMENT_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${EVAC_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/${encodeURIComponent(
        deviceRef
    )}`

export const EVAC_NEW_VERSION_NAV_LINK = (deviceRef: string): string =>
    `${EVAC_NEW_VERSION_BASE}/${encodeURIComponent(deviceRef)}`

// ******************************************************************
// EVDC Routes
// ******************************************************************/

// Publish new
export const EVDC_PUBLISH_DEVICE = "/evdc-publish-device"

// Amend device
export const EVDC_AMEND_DEVICE_BASE = "/evdc-amend-device"
export const EVDC_AMEND_DEVICE = `${EVDC_AMEND_DEVICE_BASE}/:deviceRef`
export const EVDC_AMEND_DEVICE_NAV_LINK = (deviceRef: string): string =>
    `${EVDC_AMEND_DEVICE_BASE}/${encodeURIComponent(deviceRef)}`

// New version
export const EVDC_NEW_VERSION_BASE = "/evdc-new-version"
export const EVDC_NEW_VERSION = `${EVDC_NEW_VERSION_BASE}/:deviceRef`
export const EVDC_NEW_VERSION_NAV_LINK = (deviceRef: string): string =>
    `${EVDC_NEW_VERSION_BASE}/${encodeURIComponent(deviceRef)}`

// Draft version
export const EVDC_CONTINUE_DEVICE_VERSION_DRAFT_BASE = "/evdc-continue-version"
export const EVDC_CONTINUE_DEVICE_VERSION_DRAFT = `${EVDC_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/:deviceRef`
export const EVDC_CONTINUE_DEVICE_VERSION_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${EVDC_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/${encodeURIComponent(
        deviceRef
    )}`

// Draft amendment
export const EVDC_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE =
    "/evdc-continue-amendment"
export const EVDC_CONTINUE_DEVICE_AMENDMENT_DRAFT = `${EVDC_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/:deviceRef`
export const EVDC_CONTINUE_DEVICE_AMENDMENT_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${EVDC_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/${encodeURIComponent(
        deviceRef
    )}`

// ******************************************************************
// HP Routes
// ******************************************************************/
export const HP_PUBLISH_DEVICE = "/hp-publish-device"
export const HP_AMEND_DEVICE_BASE = "/hp-amend-device"
export const HP_AMEND_DEVICE = `${HP_AMEND_DEVICE_BASE}/:deviceRef`

export const HP_AMEND_DEVICE_NAV_LINK = (deviceRef: string): string =>
    `${HP_AMEND_DEVICE_BASE}/${encodeURIComponent(deviceRef)}`

// New version
export const HP_NEW_VERSION_BASE = "/hp-new-version"
export const HP_NEW_VERSION = `${HP_NEW_VERSION_BASE}/:deviceRef`
export const HP_NEW_VERSION_NAV_LINK = (deviceRef: string): string =>
    `${HP_NEW_VERSION_BASE}/${encodeURIComponent(deviceRef)}`

// Draft version
export const HP_CONTINUE_DEVICE_VERSION_DRAFT_BASE = "/hp-continue-version"
export const HP_CONTINUE_DEVICE_VERSION_DRAFT = `${HP_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/:deviceRef`
export const HP_CONTINUE_DEVICE_VERSION_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${HP_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/${encodeURIComponent(deviceRef)}`

// Draft amendment
export const HP_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE = "/hp-continue-amendment"
export const HP_CONTINUE_DEVICE_AMENDMENT_DRAFT = `${HP_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/:deviceRef`
export const HP_CONTINUE_DEVICE_AMENDMENT_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${HP_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/${encodeURIComponent(
        deviceRef
    )}`

// ******************************************************************
// EB Routes
// ******************************************************************/
export const EB_PUBLISH_DEVICE = "/eb-publish-device"
export const EB_AMEND_DEVICE_BASE = "/eb-amend-device"
export const EB_AMEND_DEVICE = `${EB_AMEND_DEVICE_BASE}/:deviceRef`

export const EB_AMEND_DEVICE_NAV_LINK = (deviceRef: string): string =>
    `${EB_AMEND_DEVICE_BASE}/${encodeURIComponent(deviceRef)}`

// New version
export const EB_NEW_VERSION_BASE = "/eb-new-version"
export const EB_NEW_VERSION = `${EB_NEW_VERSION_BASE}/:deviceRef`
export const EB_NEW_VERSION_NAV_LINK = (deviceRef: string): string =>
    `${EB_NEW_VERSION_BASE}/${encodeURIComponent(deviceRef)}`

// Draft version
export const EB_CONTINUE_DEVICE_VERSION_DRAFT_BASE = "/eb-continue-version"
export const EB_CONTINUE_DEVICE_VERSION_DRAFT = `${EB_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/:deviceRef`
export const EB_CONTINUE_DEVICE_VERSION_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${EB_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/${encodeURIComponent(deviceRef)}`

// Draft amendment
export const EB_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE = "/eb-continue-amendment"
export const EB_CONTINUE_DEVICE_AMENDMENT_DRAFT = `${EB_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/:deviceRef`
export const EB_CONTINUE_DEVICE_AMENDMENT_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${EB_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/${encodeURIComponent(
        deviceRef
    )}`

// ***********************
// ACU (Air Conditioning Unit) Routes
// ***********************
export const ACU_PUBLISH_DEVICE = "/acu-publish-device"
export const ACU_AMEND_DEVICE_BASE = "/acu-amend-device"
export const ACU_AMEND_DEVICE = `${ACU_AMEND_DEVICE_BASE}/:deviceRef`

export const ACU_AMEND_DEVICE_NAV_LINK = (deviceRef: string): string =>
    `${ACU_AMEND_DEVICE_BASE}/${encodeURIComponent(deviceRef)}`

// New version
export const ACU_NEW_VERSION_BASE = "/acu-new-version"
export const ACU_NEW_VERSION = `${ACU_NEW_VERSION_BASE}/:deviceRef`
export const ACU_NEW_VERSION_NAV_LINK = (deviceRef: string): string =>
    `${ACU_NEW_VERSION_BASE}/${encodeURIComponent(deviceRef)}`

// Draft version
export const ACU_CONTINUE_DEVICE_VERSION_DRAFT_BASE = "/acu-continue-version"
export const ACU_CONTINUE_DEVICE_VERSION_DRAFT = `${ACU_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/:deviceRef`
export const ACU_CONTINUE_DEVICE_VERSION_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${ACU_CONTINUE_DEVICE_VERSION_DRAFT_BASE}/${encodeURIComponent(deviceRef)}`

// Draft amendment
export const ACU_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE =
    "/acu-continue-amendment"
export const ACU_CONTINUE_DEVICE_AMENDMENT_DRAFT = `${ACU_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/:deviceRef`
export const ACU_CONTINUE_DEVICE_AMENDMENT_DRAFT_NAV_LINK = (
    deviceRef: string
): string =>
    `${ACU_CONTINUE_DEVICE_AMENDMENT_DRAFT_BASE}/${encodeURIComponent(
        deviceRef
    )}`
